import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import { reaction, toJS } from 'mobx';
import debounce from 'lodash/debounce';
import styled, { css, withTheme } from 'styled-components';

import { Collection } from 'core/model/Collection';
import Box from 'core/components/Box';
import Button from 'core/components/Button';
import Tooltip from 'core/components/Tooltip';
import Flex, { FlexColumn } from 'core/components/Flex';
import Text from 'core/components/Text';
import CELL_TYPES from 'core/components/table/CELL_TYPES';
import CELL_ACTIONS from 'core/components/table/CELL_ACTIONS';
import VirtualizedTable from 'core/components/table/VirtualizedTable';

import TabSelector from 'app/components/detailTabs/TabSelector';
import AdminTableSearch from 'app/components/admin/AdminTableSearch';
import { MAX_DETAIL_TABS } from 'app/util/constants';
import { Spinner } from 'core/components';
import { getTabPref } from 'core/util/table';
import { getColKey } from 'core/components/table/Cell';
import { Table } from 'core/components/table';
import { formatDateTime } from 'core/util/dateUtils';
import LabelMapping from 'app/components/labels/LabelMapping';
import FloatingBulkActions from './FloatingBulkActions';
import AdminTableRefreshButton from './AdminTableRefreshButton';
import AdminTablePaginator from './AdminTablePaginator';

const CONTEXT_TYPE_ADMIN_TABLE = 'admin-table';

const FilterSidebar = styled(FlexColumn)`
  transition:
    border-width 0.3s ease-in-out,
    width 0.3s ease-in-out;
  border-width: 0;
  border-radius: 4px;
  width: 0;
  padding: 0;
  margin-right: 0;
  overflow-x: hidden;
  ${(props) =>
    props.open &&
    css`
      display: flex;
      border-width: 1px;
      min-width: 280px;
      width: 280px;
      margin-right: 12px;
    `}
  box-shadow: none;
`;

/* We could enable this to get rid of things that push content down during the close transition
${props =>
  !props.open &&
  css`
    * {
      white-space: nowrap !important;
      overflow: hidden !important;
    }
  `}
*/

/**
 * This is a massive component, only a few of the props are listed right now.
 *
 * Also propTypes messes up JSDoc which is fun. So this stuff is more of a hint than anything usable.
 *
 * @property {{name: string, label?: string, options?: Array<{label: string, value: string, subOptions?: Array<{label: string, value: string}>}>, freeform?: boolean, handler?: function, component?: function}[]} [bulkActions]
 *   Each entry in `options` may carry a `subOptions` array. When any option has `subOptions`, the action renders
 *   as a Blueprint fly-out menu rather than a flat Select. Selecting a sub-option calls `handler(sub.value)`.
 *   Example: `{ label: 'Observation Deck', value: 'observationDeck', subOptions: [{ label: 'My Deck', value: 'observationDeck:42' }] }`
 * @property {boolean} [noVirtualizedTable]
 * @property {object} [containerProps]
 * @property {object} [tableContainerProps]
 * @property {string[]} [excludeSearchFilters]
 * @property {object} collection
 * @property {object[]} columns
 * @property {object} controlledFilterValues
 * @property {function} onControlledFilterValueChange
 * @property {function} [onVisibleRowsChange]
 * @property {JSX.Element | null} [tableHeaderControls]
 */
@inject('$auth', '$app', '$exports', '$setup', '$kaizen')
@withTheme
@observer
class AdminTable extends Component {
  static propTypes = {
    // use regular table instead of virtualized one, on demand
    noVirtualizedTable: PropTypes.bool,
    // props to spread on the top-most container, meant for final style adjustments
    containerProps: PropTypes.object,
    // props to spread on the table container, meant for final style adjustments
    tableContainerProps: PropTypes.object,
    // array of strings of filters to exclude from search bar integration
    excludeSearchFilters: PropTypes.array,
    collection: PropTypes.instanceOf(Collection).isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    columns: PropTypes.array.isRequired,

    /**
     * controlledFilterValues and onControlledFilterValueChange can be used by the parent Component to take control of the filterValues state.
     * You can use just controlledFilterValues to pass in an initial value for the filters (see getDerivedStateFromProps)
     * You can also pass in a function for onControlledFilterValueChange to take control of state updates. If you do, AdminTable will skip
     * the setState for filterValues and call your function instead. For an example, see app/views/alerting/Alerting.js
     */
    // eslint-disable-next-line react/no-unused-prop-types
    controlledFilterValues: PropTypes.object,
    onControlledFilterValueChange: PropTypes.func,

    // eslint-disable-next-line react/no-unused-prop-types
    defaultColumns: PropTypes.array,
    expandedRowRenderer: PropTypes.func,
    fetchCollectionOnMount: PropTypes.bool,
    filterSidebar: PropTypes.object,
    footer: PropTypes.object,
    groupSummaryLookup: PropTypes.func,
    kebabActions: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    bulkActions: PropTypes.array, // null = disabled, [] = enabled but empty
    multiSelect: PropTypes.bool,
    onRowClick: PropTypes.func,
    hideNonIdealState: PropTypes.bool,
    onSort: PropTypes.func,
    rowAlign: PropTypes.string,
    rowClass: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    rowTags: PropTypes.func,
    searchBar: PropTypes.bool,
    labelType: PropTypes.any,
    selectedLabelPlural: PropTypes.string,
    selectedLabelSingular: PropTypes.string,
    showLabelActions: PropTypes.bool,
    showColumnSelector: PropTypes.bool,
    columnSelectorPosition: PropTypes.oneOf(['header', 'toolbar']),
    // eslint-disable-next-line react/require-default-props
    maxVisibleColumns: PropTypes.number,
    sidebarToolbar: PropTypes.object,
    // eslint-disable-next-line react/require-default-props
    tabPrefId(props, propName) {
      if (props.showColumnSelector === true && (props[propName] === undefined || typeof props[propName] !== 'string')) {
        return new Error('Please provide a tabPrefId string!');
      }
      return null;
    },
    tableHeaderControls: PropTypes.object,
    tableHeaderControlsPosition: PropTypes.oneOf(['beforeSearch', 'afterSearch']),
    // eslint-disable-next-line react/require-default-props
    columnSelectorExtraContent: PropTypes.node,
    groupByControls: PropTypes.object,
    toolbarActions: PropTypes.array,
    useDiscreteFilters: PropTypes.bool,
    useLoadedTimeAgo: PropTypes.bool,
    hideHeader: PropTypes.bool,
    ariaLabel: PropTypes.string,
    showRefreshButton: PropTypes.bool,
    refreshButtonProps: PropTypes.object,
    onRefresh: PropTypes.func,
    /**
     * When true, displays a spinner on the refresh button and shows "Loading..." in the table footer.
     * Prevents the footer from displaying a final count until all data has finished loading.
     */
    loading: PropTypes.bool,
    onVisibleRowsChange: PropTypes.func,
    onSelectAll: PropTypes.func,
    pagination: PropTypes.shape({
      onPageChange: PropTypes.func.isRequired, // Callback for page change
      perPage: PropTypes.number, // Initial items per page (overridden if persisted setting available)
      perPageOptions: PropTypes.arrayOf(PropTypes.number), // Overrides for the per-page selector
      persistKey: PropTypes.string // Key for saving perPage preference
    }),
    // AI Context tracking
    contextName: PropTypes.string,
    contextType: PropTypes.string,
    getModelSummary: PropTypes.func
  };

  static defaultProps = {
    labelType: '',
    showLabelActions: false,
    noVirtualizedTable: false,
    containerProps: {},
    tableContainerProps: {},
    controlledFilterValues: undefined,
    onControlledFilterValueChange: undefined,
    excludeSearchFilters: [],
    defaultColumns: [],
    expandedRowRenderer: undefined,
    fetchCollectionOnMount: false,
    /**
     * If passing in a filterSidebar be sure to pass your components props down to the AdminFilterSidebar so that
     * its props can be correctly overridden. See the React.cloneElement statement below.
     * ie. MyFilterSidebar = props => <AdminFilterSidebar {...props} filterFields={filterFields} />;
     */
    filterSidebar: null,
    footer: null,
    groupSummaryLookup: noop,
    kebabActions: [],
    bulkActions: null,
    maxVisibleColumns: MAX_DETAIL_TABS,
    multiSelect: false,
    hideNonIdealState: false,
    onRowClick: noop,
    onSort: noop,
    rowAlign: undefined,
    rowClass: undefined,
    rowHeight: 42,
    rowTags: noop,
    searchBar: true,
    selectedLabelPlural: 'items',
    selectedLabelSingular: 'item',
    sidebarToolbar: null,
    showColumnSelector: false,
    columnSelectorPosition: 'header',
    tableHeaderControls: null,
    tableHeaderControlsPosition: 'beforeSearch',
    groupByControls: null,
    toolbarActions: [],
    useDiscreteFilters: true,
    useLoadedTimeAgo: false,
    hideHeader: false,
    ariaLabel: undefined,
    showRefreshButton: false,
    refreshButtonProps: {},
    onRefresh: undefined,
    loading: false,
    onVisibleRowsChange: undefined,
    onSelectAll: undefined,
    pagination: undefined,
    // AI Context tracking
    contextName: undefined,
    contextType: undefined,
    getModelSummary: undefined
  };

  state = {
    tableKey: 0,
    loading: false,
    autoRefresh: false,
    autoRefreshFrequency: 5 * 60 * 1000,
    filterSidebarOpen: true,
    filterValues: {},
    filterFields: [],
    columns: [],
    visibleColumns: undefined
  };

  toggleFilterButtonRef = React.createRef();

  tableRef = React.createRef();

  // Ref to the underlying VirtualizedTable/Table instance so we can trigger a
  // lightweight, non-destructive re-layout (updateGrid) instead of a full remount.
  tableComponentRef = React.createRef();

  // New ref for the stable outer wrapper
  outerContainerRef = React.createRef();

  // Non-observable column width cache (avoids re-render storms during resize)
  columnWidthsRef = {};

  handleTableContainerResize = debounce(() => {
    // Column widths are handled purely via CSS flexbox, so we don't need to remount
    // the table (which would reset the virtualized list's scroll position and, for
    // large collections, is expensive enough to cause cascading resize/remount loops
    // while the container is still animating, e.g. when a row click opens the detail
    // drawer). Just ask the virtualized grid to recompute/redraw in place.
    this.tableComponentRef.current?.updateGrid?.();
  }, 300);

  // Context update timing constants
  static CONTEXT_UPDATE_DELAY_SEARCH = 300; // Delay for search input (while typing)

  static CONTEXT_UPDATE_DELAY_STATE = 100; // Delay for state changes (MobX recalculation)

  // Store the current search value for debounced context updates
  currentSearchValue = '';

  // Debounced context update for search - prevents excessive updates while typing
  debouncedUpdateTableContext = debounce(() => {
    this.updateTableContext(this.currentSearchValue);
  }, AdminTable.CONTEXT_UPDATE_DELAY_SEARCH);

  // Debounced context update for state changes - allows MobX to finish recalculating
  debouncedUpdateTableContextForState = debounce(() => {
    this.updateTableContext();
  }, AdminTable.CONTEXT_UPDATE_DELAY_STATE);

  static getDerivedStateFromProps(props) {
    const { columns, showColumnSelector, defaultColumns, tabPrefId, controlledFilterValues } = props;
    const newState = showColumnSelector ? getTabPref(tabPrefId, columns, defaultColumns) : { columns };

    if (controlledFilterValues) {
      newState.filterValues = controlledFilterValues;
    }

    return newState;
  }

  componentDidUpdate(prevProps, prevState) {
    const { collection, contextName } = this.props;
    const { autoRefresh, filterSidebarOpen, filterValues } = this.state;

    this.initializeColumnWidths();

    if (prevState.autoRefresh !== autoRefresh) {
      if (autoRefresh) {
        this.startAutoRefresh();
      } else {
        this.endAutoRefresh();
      }
    }

    // Update context when collection changes (if context tracking is enabled)
    if (contextName) {
      const prevModelsLength = prevProps.collection?.models?.length || 0;
      const currentModelsLength = collection?.models?.length || 0;
      const prevUnfilteredLength = prevProps.collection?.unfiltered?.length || 0;
      const currentUnfilteredLength = collection?.unfiltered?.length || 0;

      // Use deep equality for filter-related checks
      const prevDiscreteFilters = prevProps.collection?.discreteFilters || [];
      const currentDiscreteFilters = collection?.discreteFilters || [];
      const discreteFiltersChanged = !isEqual(prevDiscreteFilters, currentDiscreteFilters);

      // Check if filter values have changed using deep equality
      const filterValuesChanged = !isEqual(prevState.filterValues, filterValues);

      if (
        prevProps.collection !== collection ||
        prevModelsLength !== currentModelsLength ||
        prevUnfilteredLength !== currentUnfilteredLength ||
        discreteFiltersChanged ||
        prevState.filterSidebarOpen !== filterSidebarOpen ||
        filterValuesChanged
      ) {
        // Use debounced update to allow MobX to finish filtering/recalculating
        this.debouncedUpdateTableContextForState();
      }
    }
  }

  componentDidMount() {
    const { $exports, $setup, fetchCollectionOnMount, tabPrefId, onSearch } = this.props;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (this.lastContainerWidth !== width) {
          this.lastContainerWidth = width;
          this.handleTableContainerResize();
        }
      }
    });
    if (this.outerContainerRef && this.outerContainerRef.current) {
      this.resizeObserver.observe(this.outerContainerRef.current);
    }

    if (tabPrefId) {
      const savedResizedColumns = get(toJS($setup.settings), `tabPrefs.resizedColumns.${tabPrefId}`) || {};
      this.resizedColumns = savedResizedColumns;
      // Seed columnWidthsRef from persisted widths for faster initial style application
      this.columnWidthsRef = { ...savedResizedColumns };
    } else {
      this.resizedColumns = {};
    }

    if (Object.keys(this.columnWidthsRef).length > 0) {
      this.setState((prev) => ({ tableKey: prev.tableKey + 1 }));
    }

    if (tabPrefId) {
      this.clearResizedColumnsReaction = reaction(
        () => get($setup.settings, `tabPrefs.resizedColumns.${tabPrefId}`),
        (savedData) => {
          const incomingData = savedData || {};
          // Ignore stale server responses: missing or differing keys mean an older request arrived late.
          // If incomingData is empty that's a user reset, so we skip
          const isStale =
            Object.keys(incomingData).length > 0 &&
            Object.keys(this.resizedColumns).some((key) => incomingData[key] !== this.resizedColumns[key]);
          if (isStale) {
            return;
          }
          // Shallow-copy to avoid holding a live MobX observable reference.
          this.resizedColumns = Object.assign({}, incomingData);
          this.columnWidthsRef = { ...this.resizedColumns };
          if (!savedData || Object.keys(incomingData).length === 0) {
            // Columns reflow via CSS, so just ask the grid to redraw in place rather
            // than forcing a full remount (which would reset virtualized scroll position).
            this.tableComponentRef.current?.updateGrid?.();
          }
        }
      );
    }

    this.handleToggleFilterSidebar = this.handleToggleFilterSidebar.bind(this);

    if (fetchCollectionOnMount) {
      this.fetchCollection();
    }

    if (!onSearch) {
      // only hydrate the search term from url hash state if we didn't implement a custom handler
      // @see handleSearch where setHash is skipped if onSearch is provided
      $exports.getSettings().then(({ searchTerm }) => {
        // The value came from the hash, so writing it back would only cost an extra urlHash/merge request.
        this.handleSearch(searchTerm || '', { updateHash: false });
      });
    }

    // Initial context update
    this.updateTableContext();
  }

  componentWillUnmount() {
    this.clearResizedColumnsReaction?.();
    this.endAutoRefresh();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.handleTableContainerResize.cancel?.();

    // Cancel any pending debounced operations to prevent memory leaks
    if (this.debouncedUpdateTableContext) {
      this.debouncedUpdateTableContext.cancel();
    }
    if (this.debouncedUpdateTableContextForState) {
      this.debouncedUpdateTableContextForState.cancel();
    }

    this.removeTableContext();
  }

  /**
   * Get current column width (accounts for user resizing).
   * Used by columns that need to calculate dynamic content based on available space.
   *
   * @param {string} columnKey - Column key/name/id
   * @param {number} defaultWidth - Default width if column hasn't been resized
   * @returns {number} Current width in pixels
   */
  getColumnWidth = (columnKey, defaultWidth = 200) => this.columnWidthsRef[columnKey] || defaultWidth;

  initializeColumnWidths = () => {
    if (!this.tableRef.current) {
      return;
    }

    const tableElement = this.tableRef.current;
    if (!tableElement) {
      return;
    }

    const headerCells = tableElement.querySelectorAll('.thead .th');
    const { columns } = this.state;

    if (headerCells.length === 0) {
      setTimeout(() => this.initializeColumnWidths(), 50);
      return;
    }

    const columnsByKey = {};
    columns.forEach((col) => {
      columnsByKey[getColKey(col)] = col;
    });

    let columnsUpdated = false;

    headerCells.forEach((cell) => {
      const column = columnsByKey[cell.getAttribute('data-col-key')];
      if (column) {
        const rect = cell.getBoundingClientRect();
        if (rect.width > 0 && !column.width) {
          column.width = rect.width;
          columnsUpdated = true;
        }
      }
    });

    if (columnsUpdated) {
      this.setState({ columns: [...columns] });
    }
  };

  updateTableContext = (explicitSearchTerm) => {
    const { $kaizen, collection, contextName, contextType, filterSidebar } = this.props;
    const { filterSidebarOpen } = this.state;

    // Only update context if $kaizen is available and contextName is provided
    if (!$kaizen || !contextName) {
      return;
    }

    // Get collection data
    const visibleCount = collection.models.length;

    // Use explicit search term if provided (from debounced update during typing),
    // otherwise use the component's current search value state
    const searchTerm = explicitSearchTerm !== undefined ? explicitSearchTerm : this.currentSearchValue;
    const groupBy = collection.groupBy || 'None';

    // Build context
    const contextParts = [contextName];

    // Add filter sidebar state if sidebar exists
    if (filterSidebar) {
      contextParts.push(`Filter Sidebar: ${filterSidebarOpen ? 'OPEN' : 'CLOSED'}`);
    }

    // Table display state
    contextParts.push(`SHOWING: ${visibleCount} row${visibleCount !== 1 ? 's' : ''} in table`);

    if (searchTerm) {
      contextParts.push(`Search Term: "${searchTerm}"`);
    }

    if (groupBy !== 'None') {
      contextParts.push(`Grouped By: ${groupBy}`);
    }

    // Add sample of visible data (first 5 rows)
    if (collection.models.length > 0) {
      const sampleSize = Math.min(5, collection.models.length);
      contextParts.push(`\nSample of visible rows (showing ${sampleSize} of ${visibleCount}):`);

      collection.models.slice(0, sampleSize).forEach((model, idx) => {
        // Get a summary of the model - use id, name, or first few attributes
        const id = model.get('id') || model.get('name') || `Row ${idx + 1}`;
        const summary = this.getModelSummary(model);
        contextParts.push(`  ${idx + 1}. ${id}${summary ? ` - ${summary}` : ''}`);
      });
    }

    // Update the context
    $kaizen.updateChatContext({
      type: contextType || CONTEXT_TYPE_ADMIN_TABLE,
      name: contextName,
      context: `${contextParts.join('\n')}`,
      placeholder: 'Ask about this table data',
      hidden: true
    });
  };

  getModelSummary = (model) => {
    // Try to build a meaningful summary of the model
    // This can be overridden by passing a custom function via props
    const { getModelSummary } = this.props;

    if (getModelSummary) {
      return getModelSummary(model);
    }

    // Default: try common fields
    const name = model.get('name');
    const description = model.get('description');
    const status = model.get('status');
    const type = model.get('type');

    const parts = [];
    if (name) {
      parts.push(name);
    }
    if (type) {
      parts.push(`type: ${type}`);
    }
    if (status) {
      parts.push(`status: ${status}`);
    }
    if (description && description.length < 50) {
      parts.push(description);
    }

    return parts.join(', ');
  };

  removeTableContext = () => {
    const { $kaizen, contextType } = this.props;

    if (!$kaizen) {
      return;
    }

    const oldContext = Array.from($kaizen.activeChatContext).find(
      (ctx) => ctx.type === (contextType || CONTEXT_TYPE_ADMIN_TABLE)
    );
    if (oldContext) {
      $kaizen.removeChatContext(oldContext);
    }
  };

  setAutoRefresh = (newValue) => {
    this.setState({
      autoRefresh: newValue
    });
  };

  startAutoRefresh = () => {
    const { onRefresh } = this.props;
    const { autoRefreshFrequency } = this.state;

    this.endAutoRefresh();

    if (onRefresh) {
      this.autoRefreshIntervalID = setInterval(onRefresh, autoRefreshFrequency);
    }
  };

  endAutoRefresh = () => {
    if (this.autoRefreshIntervalID) {
      clearInterval(this.autoRefreshIntervalID);
    }
  };

  fetchCollection = () => {
    const { collection } = this.props;

    this.setState({ loading: true });
    collection
      .fetch({ force: true })
      .then(() => {
        collection.totalCount = collection.totalCount || collection.size;
      })
      .finally(() => this.setState({ loading: false }));
  };

  handleRefresh = () => {
    const { onRefresh } = this.props;

    if (onRefresh) {
      this.setState({ loading: true });
      return Promise.resolve(onRefresh()).finally(() => this.setState({ loading: false }));
    }

    return this.fetchCollection();
  };

  get tableFooter() {
    const { hideResultCount, footer, pagination } = this.props;

    // Show footer driven by paged collection
    if (pagination) {
      return this.pagedCollectionFooter;
    }

    // Show only passed footer component
    if (hideResultCount) {
      return footer;
    }

    // Show infinite scroll / static results footer; can also render passed footer
    return this.resultCountFooter;
  }

  get pagedCollectionFooter() {
    const { $app, collection, pagination, footer, loading: loadingExternal, theme } = this.props;
    const { loading } = this.state;

    if (!pagination || $app.isExport) {
      return null;
    }

    const wrapperProps = {
      px: 2,
      justifyContent: 'flex-end',
      alignItems: 'center',
      borderTop: 'thin',
      bg: theme.backgrounds.tableHeader
    };

    if (loading || loadingExternal) {
      return (
        <Flex {...wrapperProps} minHeight={40}>
          <Spinner size={16} mx={1} />
          <Text small muted>
            Loading...
          </Text>
        </Flex>
      );
    }

    return (
      <Flex {...wrapperProps}>
        {footer}
        <AdminTablePaginator collection={collection} pagination={pagination} />
      </Flex>
    );
  }

  get resultCountFooter() {
    const { theme, loading: externalLoading, footer, collection, useLoadedTimeAgo } = this.props;
    const { loading } = this.state;

    return (
      <Flex
        px={2}
        bg={theme.backgrounds.tableHeader}
        textAlign="right"
        borderTop="thin"
        borderRadius="0 0 4px 4px"
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        minHeight={30}
      >
        {loading || externalLoading ? (
          <Box display="inline-flex" mr={1}>
            <Spinner size={16} mx={1} />
            <Text small muted>
              Loading...
            </Text>
          </Box>
        ) : (
          <>
            {footer}
            <Text fontWeight={500} fontSize={12} display="inline-flex">
              {!useLoadedTimeAgo && collection.requestStatus === 'fetchingMore' ? (
                <Box display="inline-flex" mr={1}>
                  <Spinner size={16} mx={1} />
                  <Text muted>Loading More...</Text>
                </Box>
              ) : null}
              {useLoadedTimeAgo ? (
                <Box display="inline-flex" mr={1}>
                  <Text muted>{`Updated ${formatDateTime(collection.lastFetched, 'HH:mm:ss')}`}</Text>
                </Box>
              ) : null}
              Showing
              {collection.size < collection.totalCount
                ? ` ${Number(collection.size)?.toLocaleString()} of ${Number(collection.totalCount)?.toLocaleString()} `
                : ` ${Number(collection.size)?.toLocaleString()} `}
              {collection.size === 1 && !(collection.size < collection.totalCount) ? 'result' : 'results'}
              {collection.hasMoreResults ? ' (More Available)' : ''}
            </Text>
          </>
        )}
      </Flex>
    );
  }

  get columns() {
    const {
      $app,
      showColumnSelector,
      columnSelectorPosition,
      kebabActions,
      kebabProps,
      multiSelect,
      multiSelectProps,
      maxVisibleColumns,
      tabPrefId
    } = this.props;
    const { columns, visibleColumns } = this.state;

    // Inject columnWidth for columns that need it
    columns.forEach((col) => {
      if (col.usesColumnWidth && col.renderer) {
        const columnKey = getColKey(col);

        if (!col._originalRenderer) {
          col._originalRenderer = col.renderer;
        }

        col.renderer = (props) => {
          const columnWidth = this.getColumnWidth(columnKey, col.width);
          return col._originalRenderer({ ...props, columnWidth });
        };
      }
    });

    if (multiSelect && !columns.find((col) => col.key === 'select')) {
      columns.unshift({
        key: 'select',
        value: 'select',
        id: 'select',
        type: CELL_TYPES.ACTION,
        actions: [CELL_ACTIONS.SELECT],
        padding: '6px 0 6px 16px !important',
        headerPadding: '4px 0 0 16px !important',
        justifyContent: 'center',
        width: 40,
        minWidth: 40,
        verticalAlign: 'top',
        ...multiSelectProps
      });
    }

    if (kebabActions.length > 0 && !columns.find((col) => col.key === 'kebab')) {
      columns.push({
        key: 'kebab',
        value: 'kebab',
        id: 'kebab',
        type: CELL_TYPES.ACTION,
        actions: [CELL_ACTIONS.KEBAB(kebabActions)],
        padding: '6px 12px 6px 16px !important',
        justifyContent: 'center',
        width: 40,
        minWidth: 40,
        ...kebabProps
      });
    }

    if (!$app.isExport && showColumnSelector && columnSelectorPosition !== 'toolbar') {
      const activeColumns = columns.map((column) => {
        if (visibleColumns) {
          return {
            ...column,
            visible:
              column.forceVisible ||
              column.type === CELL_TYPES.ACTION ||
              column.key === 'select' ||
              visibleColumns.includes(getColKey(column)),
            always_active: column.type === CELL_TYPES.ALWAYS_ACTIVE
          };
        }
        return column;
      });

      const tabSelector = (tabSelectorProps) => (
        <TabSelector
          tabs={activeColumns.filter(
            (col) => col.type === CELL_TYPES.VALUE || col.type === CELL_TYPES.ALWAYS_ACTIVE || !col.type
          )}
          visibleTabs={visibleColumns}
          maxTabs={maxVisibleColumns}
          onChange={this.handleColumnsChange}
          buttonProps={{ small: true, intent: 'none', minimal: false }}
          entityName="columns"
          tabPrefId={tabPrefId}
          {...tabSelectorProps}
        />
      );
      const lastColumn = activeColumns[activeColumns.length - 1];

      if (lastColumn?.type === CELL_TYPES.ACTION) {
        lastColumn.width = Math.max(lastColumn.width || 0, 100);
        lastColumn.label = tabSelector(lastColumn.tabSelectorProps);
        lastColumn.paddingRight = 0;
      } else {
        activeColumns.push({
          type: CELL_TYPES.ACTION,
          width: 100,
          label: tabSelector(),
          paddingRight: 0,
          actions: [],
          visible: true
        });
      }

      return activeColumns.filter((col) => col.visible);
    }

    // When columnSelector is in toolbar position, still filter by visibility
    if (!$app.isExport && showColumnSelector && columnSelectorPosition === 'toolbar' && visibleColumns) {
      const filtered = columns.filter(
        (col) =>
          col.forceVisible ||
          col.type === CELL_TYPES.ACTION ||
          col.key === 'select' ||
          visibleColumns.includes(getColKey(col))
      );
      filtered.forEach((col) => {
        col.padding = col.padding ?? '6px 12px';
      });
      return filtered;
    }

    columns.forEach((col) => {
      col.padding = col.padding ?? '6px 12px';
    });

    return columns;
  }

  handleColumnsChange = (columns) => {
    const { $setup, tabPrefId } = this.props;
    const tabPrefs = get($setup, 'settings.tabPrefs') || {};

    const { columns: prevColumns } = this.state;
    const [firstColumn] = prevColumns;
    const [lastColumn] = prevColumns.slice(-1);

    if (firstColumn.type === CELL_TYPES.ACTION) {
      columns.unshift(firstColumn);
    }
    if (lastColumn.type === CELL_TYPES.ACTION) {
      columns.push(lastColumn);
    }

    tabPrefs[tabPrefId] = columns.map((col) => ({ id: getColKey(col), visible: col.visible }));
    $setup.updateSettings({ tabPrefs });
  };

  handleToggleFilterSidebar = () => {
    const { filterSidebarOpen } = this.state;
    if (this?.toggleFilterButtonRef?.current?.buttonRef) {
      this.toggleFilterButtonRef.current.buttonRef.focus();
    }

    this.setState({ filterSidebarOpen: !filterSidebarOpen });
  };

  handleSearch = (value, { updateHash = true } = {}) => {
    const { collection, onSearch, guardForDuplicates, contextName, $exports } = this.props;
    const searchValue = value;

    // If it needs to be handled externally e.g. server-side
    if (onSearch) {
      onSearch(searchValue);
      return;
    }

    // Update hash immediately for URL state
    if (updateHash) {
      $exports.setHash({ searchTerm: searchValue });
    }

    // Filter collection immediately (already fast with MobX)
    collection.filter(searchValue, { guardForDuplicates });

    // Update context if tracking is enabled (debounced to avoid excessive AI updates while typing)
    if (contextName) {
      // Store the search value so debounced update can use it
      this.currentSearchValue = searchValue;
      this.debouncedUpdateTableContext();
    }
  };

  handleRemoveFilter = (filterType) => {
    const { $exports, collection, useDiscreteFilters, onControlledFilterValueChange } = this.props;
    const { filterValues } = this.state;
    const newFilterValues = { ...filterValues };

    newFilterValues[filterType] = '';

    if (onControlledFilterValueChange) {
      onControlledFilterValueChange(newFilterValues);
    } else {
      this.setState({ filterValues: newFilterValues });
    }

    if (useDiscreteFilters) {
      collection.removeDiscreteFilter(filterType);
      collection.filter();
    }

    $exports.getSettings().then(({ hashedFilters }) => {
      if (!hashedFilters) {
        return;
      }

      delete hashedFilters[filterType];
      $exports.setHash({ hashedFilters });
    });
  };

  handleFilterStateChange = (filterValues, filterFields) => {
    const { onControlledFilterValueChange, contextName } = this.props;

    if (onControlledFilterValueChange) {
      onControlledFilterValueChange(filterValues);
      this.setState({ filterFields }, () => {
        // Update context after filter change if context tracking is enabled
        // Use debounced update to allow MobX to finish recalculating
        if (contextName) {
          this.debouncedUpdateTableContextForState();
        }
      });
    } else {
      this.setState({ filterValues, filterFields }, () => {
        // Update context after filter change if context tracking is enabled
        // Use debounced update to allow MobX to finish recalculating
        if (contextName) {
          this.debouncedUpdateTableContextForState();
        }
      });
    }
  };

  handleLabelPopoverClosed = (itemsHaveBeenUpdated) => {
    const { collection } = this.props;

    if (itemsHaveBeenUpdated) {
      collection.fetch({ force: true });
    }
  };

  handleRowClick = (model, e) => {
    const { onRowClick } = this.props;
    e.stopPropagation();
    onRowClick(model, e);
  };

  get refreshButton() {
    const { collection, refreshButtonProps, showAutoRefresh, loading: externalLoading, showRefreshButton } = this.props;
    const { loading, autoRefreshFrequency, autoRefresh } = this.state;
    const { lastFetched } = collection;

    if (!showRefreshButton) {
      return null;
    }

    const tooltipContent = (
      <>
        <Text as="div">Refresh results</Text>
        {lastFetched && (
          <Text as="div" small muted>
            Last fetched: {formatDateTime(lastFetched)}
          </Text>
        )}
      </>
    );

    return (
      <Tooltip content={tooltipContent}>
        <AdminTableRefreshButton
          loading={loading || externalLoading}
          autoRefreshEnabled={autoRefresh}
          buttonProps={refreshButtonProps}
          onRefresh={this.handleRefresh}
          allowAutoRefresh={showAutoRefresh}
          setAutoRefresh={this.setAutoRefresh}
          setRefreshFrequency={this.handleRefreshFrequencyChange}
          refreshFrequency={autoRefreshFrequency}
        />
      </Tooltip>
    );
  }

  get toolbarColumnSelector() {
    const {
      showColumnSelector,
      columnSelectorPosition,
      maxVisibleColumns,
      $app,
      tabPrefId,
      columnSelectorExtraContent
    } = this.props;
    const { columns, visibleColumns } = this.state;

    if (!showColumnSelector || columnSelectorPosition !== 'toolbar' || $app.isExport) {
      return null;
    }

    const tabs = columns
      .filter((col) => col.type === CELL_TYPES.VALUE || col.type === CELL_TYPES.ALWAYS_ACTIVE || !col.type)
      .map((col) => {
        const colKey = getColKey(col);
        const id = col.id ?? colKey;
        return {
          ...col,
          id,
          value: col.value ?? id,
          visible: !visibleColumns || visibleColumns.includes(colKey) || !!col.forceVisible,
          always_active: col.type === CELL_TYPES.ALWAYS_ACTIVE
        };
      });

    return (
      <TabSelector
        tabs={tabs}
        visibleTabs={visibleColumns}
        maxTabs={maxVisibleColumns}
        onChange={this.handleColumnsChange}
        buttonProps={{ intent: 'none', minimal: false }}
        entityName="columns"
        tabPrefId={tabPrefId}
        extraContent={columnSelectorExtraContent}
      />
    );
  }

  handleRefreshFrequencyChange = (value) => {
    this.setState(
      {
        autoRefreshFrequency: value
      },
      () => {
        this.endAutoRefresh();

        if (value) {
          this.startAutoRefresh();
        }
      }
    );
  };

  render() {
    const {
      ariaLabel,
      noVirtualizedTable,
      excludeSearchFilters,
      collection,
      useDiscreteFilters,
      labelType,
      bulkActions,
      showLabelActions,
      toolbarActions,
      infiniteScroll,
      expandedRowRenderer,
      groupSummaryLookup,
      groupFn,
      hideNonIdealState,
      rowKeyField,
      rowAlign,
      rowClass,
      rowHeight,
      rowTags,
      width,
      selectedLabelSingular,
      selectedLabelPlural,
      tableHeaderControls,
      tableHeaderControlsPosition,
      tabPrefId,
      groupByControls,
      filterSidebar,
      searchBar,
      sidebarToolbar,
      onRowClick,
      onSort,
      theme,
      containerProps,
      searchTerm,
      tableContainerProps,
      hideHeader,
      isCollapsed,
      emptyState,
      onVisibleRowsChange,
      onSelectAll,
      $auth,
      $app,
      $setup
    } = this.props;
    const { filterSidebarOpen, filterValues, filterFields, tableKey } = this.state;
    const showToolbarActions = toolbarActions.length > 0 && collection.selected?.length > 0;

    // Do not remove. I'm needed for mobx to trigger re-render when settings change!
    // eslint-disable-next-line no-unused-expressions
    $setup.settings;

    const TableComponent = $app.isExport || noVirtualizedTable ? Table : VirtualizedTable;
    // Clipping containers would cut off rows that paginate across printed pages
    const overflow = $app.isExport ? 'visible' : 'hidden';

    const ariaTableProps = {};
    if (ariaLabel) {
      ariaTableProps.ariaLabel = ariaLabel;
    }

    return (
      <Flex flex={1} overflow={overflow} p="1px" ref={this.outerContainerRef} {...containerProps}>
        <FlexColumn flex="1 1 0%" width={width} maxWidth="100%">
          {(sidebarToolbar || filterSidebar || searchBar || tableHeaderControls) && (
            <Flex py="3px" mb="5px" gap="5px" alignItems="center" overflow="hidden">
              {!$app.isExport && (sidebarToolbar || filterSidebar) && this.toggleFilterButtonRef && (
                <Button
                  minimal
                  icon="filter"
                  color={filterSidebarOpen ? 'primary' : undefined}
                  active={filterSidebarOpen}
                  onClick={this.handleToggleFilterSidebar}
                  ariaLabel="Toggle Filter Sidebar"
                  ariaExpanded={filterSidebarOpen}
                  aria-controls="filter-sidebar"
                  ref={this.toggleFilterButtonRef}
                />
              )}
              {groupByControls && <Box>{groupByControls}</Box>}
              {tableHeaderControls && tableHeaderControlsPosition === 'beforeSearch' && (
                <Box>{tableHeaderControls}</Box>
              )}
              {searchBar && (
                <AdminTableSearch
                  excludeFilters={excludeSearchFilters}
                  collection={collection}
                  filterFields={filterFields}
                  filterValues={filterValues}
                  onSearch={this.handleSearch}
                  onRemoveFilter={this.handleRemoveFilter}
                  searchTerm={searchTerm}
                />
              )}
              {this.toolbarColumnSelector}
              {tableHeaderControls && tableHeaderControlsPosition === 'afterSearch' && <Box>{tableHeaderControls}</Box>}
              {this.refreshButton}
            </Flex>
          )}

          <Flex flex={1} overflow={overflow}>
            {(sidebarToolbar || filterSidebar) && (
              <FilterSidebar
                open={filterSidebarOpen}
                border="thinLighter"
                id="filter-sidebar"
                aria-hidden={!filterSidebarOpen}
              >
                {sidebarToolbar}
                {filterSidebar && (
                  <Flex flex={1} overflow="hidden" boxShadow="none">
                    {React.cloneElement(filterSidebar, {
                      onClose: this.handleToggleFilterSidebar,
                      onFilterStateChange: this.handleFilterStateChange,
                      useDiscreteFilters,
                      showClose: true,
                      collection,
                      filterValues,
                      filterSidebarOpen
                    })}
                  </Flex>
                )}
              </FilterSidebar>
            )}

            <FlexColumn flex="1 1 0%" overflow={overflow}>
              {showToolbarActions && (
                <Box mb={1}>
                  <Flex flex={1} alignItems="center">
                    {showLabelActions && $auth.isAdministrator && (
                      <Box mr={1}>
                        <LabelMapping
                          showSelected={false}
                          collection={collection}
                          type={labelType}
                          onPopoverClosed={this.handleLabelPopoverClosed}
                        />
                      </Box>
                    )}
                    {toolbarActions.map((action) => {
                      if (action.component) {
                        return (
                          <Box mr={1} key={action.id} {...action.containerProps}>
                            {action.component}
                          </Box>
                        );
                      }
                      return (
                        <Button
                          key={action.id}
                          text={action.label}
                          onClick={action.handler}
                          intent={action.intent}
                          mr={1}
                          disabled={action.disabled}
                          icon={action.icon}
                          loading={action.loading}
                        />
                      );
                    })}
                    {collection.selected?.length}{' '}
                    {collection.selected?.length > 1 ? selectedLabelPlural : selectedLabelSingular} selected
                  </Flex>
                </Box>
              )}
              <FlexColumn
                ref={this.tableRef}
                flex="1 1 0%"
                overflow={overflow}
                borderRadius="4px"
                border="thinLighter"
                position="relative"
                {...tableContainerProps}
              >
                <TableComponent
                  key={tableKey}
                  ref={this.tableComponentRef}
                  {...ariaTableProps}
                  headerStyle={{
                    alignItems: 'center',
                    background: theme.backgrounds.tableHeader,
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                    fontSize: '12px',
                    paddingBottom: '4px',
                    paddingTop: '4px',
                    borderBottom: theme.borders.thin
                  }}
                  infiniteScroll={infiniteScroll}
                  useSortTooltips
                  noHighlightSorted
                  collection={collection}
                  columns={this.columns}
                  expandedRowRenderer={expandedRowRenderer}
                  tabPrefId={tabPrefId}
                  groupFn={groupFn}
                  groupSummaryLookup={groupSummaryLookup}
                  resizedColumns={this.resizedColumns}
                  columnWidthsRef={this.columnWidthsRef}
                  rowKeyField={rowKeyField}
                  rowAlign={rowAlign}
                  rowClass={rowClass}
                  rowHeight={rowHeight}
                  rowTags={rowTags}
                  onRowClick={onRowClick && onRowClick !== noop ? this.handleRowClick : null}
                  onSort={onSort}
                  hideNonIdealState={hideNonIdealState}
                  selectOnRowClick={false}
                  isCollapsed={isCollapsed}
                  hideHeader={hideHeader}
                  emptyState={emptyState}
                  onVisibleRowsChange={onVisibleRowsChange}
                  onSelectAll={onSelectAll}
                  flexed
                />
                {this.tableFooter}
                {Array.isArray(bulkActions) && (
                  <FloatingBulkActions
                    collection={collection}
                    actions={bulkActions}
                    labelType={labelType}
                    selectedLabelSingular={selectedLabelSingular}
                    selectedLabelPlural={selectedLabelPlural}
                    toolbarActions={toolbarActions}
                  />
                )}
              </FlexColumn>
            </FlexColumn>
          </Flex>
        </FlexColumn>
      </Flex>
    );
  }
}

export default AdminTable;
